import React from 'react';

// Structured error log — accessible from Diagnostics
export interface CrashEntry {
  module: string;
  error: string;
  componentStack: string;
  timestamp: number;
}

// Module-level crash history (persists across re-renders, not across page loads)
const crashHistory: CrashEntry[] = [];
export function getCrashHistory(): readonly CrashEntry[] { return crashHistory; }
export function clearCrashHistory() { crashHistory.length = 0; }

interface Props { children: React.ReactNode; module?: string }
interface State { hasError: boolean; error: Error | null }

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) { super(props); this.state = { hasError: false, error: null }; }

  static getDerivedStateFromError(error: Error): State { return { hasError: true, error }; }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const entry: CrashEntry = {
      module: this.props.module || 'unknown',
      error: error.message,
      componentStack: info.componentStack || '',
      timestamp: Date.now(),
    };
    crashHistory.push(entry);
    // Keep only last 50
    if (crashHistory.length > 50) crashHistory.splice(0, crashHistory.length - 50);
    // Structured log
    console.error(`[Crash:${entry.module}]`, { error: error.message, stack: error.stack, componentStack: info.componentStack });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ padding: 24, background: 'var(--red-bg)', border: '1px solid var(--red-border)', borderRadius: 'var(--radius-lg)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', marginBottom: 8 }}>
          ● {this.props.module ? `${this.props.module} crashed` : 'Component crashed'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginBottom: 12 }}>{this.state.error?.message}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button onClick={() => this.setState({ hasError: false, error: null })} style={{
            padding: '6px 16px', background: 'var(--bg-active)', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
          }}>Retry</button>
          <button onClick={() => window.location.reload()} style={{
            padding: '6px 16px', background: 'transparent', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text-ghost)', fontSize: 12, cursor: 'pointer',
          }}>Reload page</button>
        </div>
      </div>
    );
  }
}
